import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Users, TrendingUp, AlertTriangle, CheckCircle2, RefreshCcw, Mail, MessageCircle, Crosshair, Gauge, Vault, Radar, Film, Trophy, Calendar as CalendarIcon } from "lucide-react";
import CustomTraining from "@/components/CustomTraining";
import LeaderPledge from "@/components/LeaderPledge";
import OperationalBlueprint from "@/components/OperationalBlueprint";
import CoordinatorInbox from "@/components/CoordinatorInbox";
import StrikeList from "@/components/StrikeList";
import TitanArsenal from "@/components/TitanArsenal";
import ScoutReports from "@/components/ScoutReports";
import AlfredShowreel from "@/components/AlfredShowreel";
import RangerAcademyCalendar from "@/components/RangerAcademyCalendar";
import LeadHunter from "@/components/LeadHunter";
import PracticeModeToggle from "@/components/PracticeModeToggle";
import PracticeModeBanner from "@/components/PracticeModeBanner";
import { GridTransferBanner, TransferWindow } from "@/components/TransferWindow";
import ReplayGreeting from "@/components/ReplayGreeting";
import { isGridInitialized, speakAs } from "@/lib/audio";

const VOTE_URL = "https://app.emergent.sh/showcase/wingman-academy";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const STATUS_CFG = {
  green: { label: "Locked In", color: "#34D399", glow: "glow-green" },
  yellow: { label: "Drifting", color: "#FBBF24", glow: "glow-yellow" },
  red: { label: "Stalled", color: "#F43F5E", glow: "glow-red" },
};

export default function LeaderDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const t = location.state?.tab;
    return t === "strike" || t === "arsenal" || t === "scout" || t === "showreel" || t === "ranger" ? t : "ops";
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leader/team");
      setData(data);
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Fire Alfred's spoken welcome if the audio gate has been unlocked this session.
  useEffect(() => {
    if (isGridInitialized()) {
      const t = setTimeout(() => speakAs("alfred"), 700);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#060B14]">
      <PracticeModeBanner />
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        {/* Last 50 Mile · AI Utility hero sentence */}
        <div
          className="mb-6 sm:mb-8 rounded-sm border px-4 sm:px-7 py-4 relative overflow-hidden"
          style={{
            borderColor: "rgba(212,175,55,0.4)",
            background:
              "linear-gradient(90deg, rgba(212,175,55,0.1) 0%, rgba(6,11,20,0.6) 55%, rgba(212,175,55,0.06) 100%)",
          }}
          data-testid="leader-utility-hero"
        >
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,175,55,0.85), transparent)",
            }}
          />
          <div className="text-[10px] uppercase tracking-[0.34em] text-gold mb-1.5 flex items-center gap-2">
            <span
              className="w-1.5 h-1.5 rounded-full light-pulse"
              style={{ backgroundColor: "#D4AF37" }}
            />
            The Last 50 Mile · AI Utility Company
          </div>
          <p
            className="font-display text-sm sm:text-base text-white leading-snug"
            data-testid="last-50-mile-hero-sentence"
          >
            Welcome to the Last 50 Mile AI Utility Company. Before we show you what AI can do,
            we&apos;d like to show you what it{" "}
            <span className="text-gold italic">feels</span> like.
          </p>
          <div className="mt-3">
            <ReplayGreeting character="alfred" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="hud-line w-10" />
              <span className="hud-label">Business Operations · Your AI Utility Team</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-white font-light">
              Squadron led by <span className="text-gold italic">{user?.full_name?.split(" ")[0]}</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2">Real-time team momentum &amp; warning lights across the AI utility grid</p>
          </div>
          {activeTab === "ops" && (
            <div className="flex flex-col xs:flex-row sm:flex-row items-stretch xs:items-center gap-2 w-full sm:w-auto">
              <PracticeModeToggle defaultOn={false} compact />
              <a
                href={VOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leader-vote-button"
                className="btn-gold px-5 py-3 rounded-sm text-xs sm:text-sm flex items-center justify-center gap-2 uppercase tracking-[0.14em] sm:tracking-[0.18em] min-h-[44px]"
              >
                <Trophy size={14} /> Vote for Wingman Alfred
              </a>
              <button onClick={fetchData} data-testid="leader-refresh-button" className="btn-ghost-gold px-5 py-3 rounded-sm text-sm flex items-center justify-center gap-2 min-h-[44px]">
                <RefreshCcw size={14} /> Refresh
              </button>
            </div>
          )}
        </div>

        {/* Tab strip — horizontally scrollable on mobile */}
        <div
          className="flex items-center gap-1 sm:gap-2 mb-8 border-b border-white/10 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10"
          data-testid="dashboard-tabs"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <TabButton
            active={activeTab === "ops"}
            onClick={() => setActiveTab("ops")}
            icon={<Gauge size={14} />}
            label="Squadron Ops"
            testId="tab-ops"
          />
          <TabButton
            active={activeTab === "strike"}
            onClick={() => setActiveTab("strike")}
            icon={<Crosshair size={14} />}
            label="Strike List"
            testId="tab-strike"
            accent="#F43F5E"
          />
          <TabButton
            active={activeTab === "arsenal"}
            onClick={() => setActiveTab("arsenal")}
            icon={<Vault size={14} />}
            label="Titan Arsenal"
            testId="tab-arsenal"
            accent="#D4AF37"
          />
          <TabButton
            active={activeTab === "scout"}
            onClick={() => setActiveTab("scout")}
            icon={<Radar size={14} />}
            label="Scout Reports"
            testId="tab-scout"
            accent="#10B981"
          />
          <TabButton
            active={activeTab === "showreel"}
            onClick={() => setActiveTab("showreel")}
            icon={<Film size={14} />}
            label="Alfred Showreel"
            testId="tab-showreel"
            accent="#D4AF37"
          />
          <TabButton
            active={activeTab === "ranger"}
            onClick={() => setActiveTab("ranger")}
            icon={<CalendarIcon size={14} />}
            label="Campaign Calendar"
            testId="tab-ranger"
            accent="#F5C842"
          />
        </div>

        {activeTab === "strike" ? (
          <StrikeList />
        ) : activeTab === "arsenal" ? (
          <TitanArsenal />
        ) : activeTab === "scout" ? (
          <ScoutReports />
        ) : activeTab === "showreel" ? (
          <AlfredShowreel />
        ) : activeTab === "ranger" ? (
          <RangerAcademyCalendar />
        ) : (
          <>
        {/* Grid Transfer celebration banner */}
        <GridTransferBanner />

        {/* Leader's Pledge */}
        <div className="mb-8">
          <LeaderPledge />
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="leader-stats">
          <StatBlock icon={<Users size={16} />} label="Recruits" value={data?.stats?.total_recruits ?? 0} testId="stat-total-recruits" />
          <StatBlock icon={<CheckCircle2 size={16} className="text-[#34D399]" />} label="Locked In" value={data?.stats?.green ?? 0} accent="#34D399" testId="stat-green" />
          <StatBlock icon={<AlertTriangle size={16} className="text-[#FBBF24]" />} label="Drifting" value={data?.stats?.yellow ?? 0} accent="#FBBF24" testId="stat-yellow" />
          <StatBlock icon={<AlertTriangle size={16} className="text-[#F43F5E]" />} label="Stalled" value={data?.stats?.red ?? 0} accent="#F43F5E" testId="stat-red" />
          <StatBlock icon={<TrendingUp size={16} />} label="Avg Hrs" value={`${data?.stats?.avg_hours ?? 0}h`} testId="stat-avg" />
        </div>

        {/* Team list + Transfer Window sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="glass-strong rounded-sm overflow-hidden lg:col-span-2">
          <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
            <span className="hud-label">Team Momentum</span>
            <div className="flex items-center gap-4 text-[10px] text-slate-400">
              <Legend color="#34D399" label="100% Hit" />
              <Legend color="#FBBF24" label="<50% / Missed 1d" />
              <Legend color="#F43F5E" label="Missed 48h" />
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading squadron...</div>
          ) : !data?.team?.length ? (
            <div className="p-10 text-center text-slate-500" data-testid="leader-empty-team">
              No recruits enlisted yet. Recruits will appear here after they complete checkout.
            </div>
          ) : (
            <div className="divide-y divide-white/5" data-testid="team-list">
              {data.team.map((r) => (
                <RecruitRow key={r.id} recruit={r} />
              ))}
            </div>
          )}
          </section>

          <div className="lg:col-span-1">
            <TransferWindow />
          </div>
        </div>

        {/* Custom Training */}
        <div className="mt-8">
          <CustomTraining />
        </div>

        {/* Lead Hunter — AI Salesperson command box */}
        <div className="mt-8">
          <LeadHunter />
        </div>

        {/* Coordinator Inbox (Local Foyer leads) */}
        <div className="mt-8">
          <CoordinatorInbox />
        </div>

        {/* Operational Blueprint */}
        <div className="mt-8">
          <OperationalBlueprint />
        </div>
          </>
        )}
      </main>
    </div>
  );
}

function RecruitRow({ recruit }) {
  const cfg = STATUS_CFG[recruit.warning_status];
  const initials = (recruit.full_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const lastLoggedLabel = recruit.last_logged
    ? new Date(recruit.last_logged).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "Never";

  return (
    <div className="px-6 py-5 grid grid-cols-12 gap-4 items-center hover:bg-white/5 transition-colors" data-testid={`recruit-row-${recruit.id}`}>
      <div className="col-span-12 sm:col-span-4 flex items-center gap-4">
        <div
          className={`w-3 h-3 rounded-full ${cfg.glow} light-pulse`}
          style={{ backgroundColor: cfg.color }}
          data-testid={`warning-light-${recruit.id}`}
          title={cfg.label}
        />
        <div className="w-10 h-10 rounded-sm border border-white/10 flex items-center justify-center font-display text-sm text-gold">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-white font-display text-sm truncate">{recruit.full_name}</div>
          <div className="text-xs text-slate-500 truncate">{recruit.email}</div>
        </div>
      </div>

      <div className="col-span-6 sm:col-span-3">
        <div className="hud-label text-[9px] mb-1.5">Week Progress</div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-white/5 rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm transition-all"
              style={{ width: `${Math.min(100, recruit.progress_pct)}%`, backgroundColor: cfg.color }}
            />
          </div>
          <span className="text-xs text-white font-mono-aviation">{recruit.week_hours.toFixed(1)}h</span>
        </div>
      </div>

      <div className="col-span-6 sm:col-span-3">
        <div className="hud-label text-[9px] mb-1">Last Log</div>
        <div className="text-xs text-slate-400 font-mono-aviation">{lastLoggedLabel}</div>
      </div>

      <div className="col-span-12 sm:col-span-2 flex sm:justify-end">
        <PushMessageDialog recruit={recruit} />
      </div>
    </div>
  );
}

function PushMessageDialog({ recruit }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CFG[recruit.warning_status];
  const defaultMsg =
    recruit.warning_status === "red"
      ? `Hey ${recruit.full_name.split(" ")[0]}, the Architect noticed you haven't logged in 48+ hours. Everything OK? Let's get you back in formation — even 15 minutes today restarts the climb.`
      : recruit.warning_status === "yellow"
      ? `Hey ${recruit.full_name.split(" ")[0]}, you're at ${recruit.week_hours.toFixed(1)}h this week. You can close the gap to 7 with focused sessions today and tomorrow. I'm here if you need direction.`
      : `Hey ${recruit.full_name.split(" ")[0]}, you crushed it this week — ${recruit.week_hours.toFixed(1)}h logged. Keep stacking these and you'll hit Consistent Creator soon.`;

  const [msg, setMsg] = useState(defaultMsg);

  const sendEmail = () => {
    const subject = encodeURIComponent("Wingman Alfred · Quick check-in");
    const body = encodeURIComponent(msg);
    window.location.href = `mailto:${recruit.email}?subject=${subject}&body=${body}`;
    setOpen(false);
  };

  const sendWhatsapp = () => {
    const text = encodeURIComponent(msg);
    window.open(`https://wa.me/?text=${text}`, "_blank");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`send-push-button-${recruit.id}`}
          className="btn-ghost-gold px-4 py-2 rounded-sm text-xs flex items-center gap-2"
        >
          <Send size={12} /> Push Message
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A1122] border-white/10 text-white max-w-lg" data-testid="push-message-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.color }} />
            Push to {recruit.full_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="hud-label text-[10px]">Status: {cfg.label}</div>
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={6}
            data-testid="push-message-textarea"
            className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-gold resize-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={sendEmail}
              data-testid="push-message-email-button"
              className="btn-gold py-3 rounded-sm flex items-center justify-center gap-2 text-sm"
            >
              <Mail size={14} /> Email
            </button>
            <button
              onClick={sendWhatsapp}
              data-testid="push-message-whatsapp-button"
              className="btn-ghost-gold py-3 rounded-sm flex items-center justify-center gap-2 text-sm"
            >
              <MessageCircle size={14} /> WhatsApp
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const StatBlock = ({ icon, label, value, accent, testId }) => (
  <div className="glass rounded-sm p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2 text-slate-400">{icon}<span className="hud-label text-[9px]">{label}</span></div>
    <div className="font-display text-3xl text-white" style={accent ? { color: accent } : {}}>{value}</div>
  </div>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    <span>{label}</span>
  </div>
);

const TabButton = ({ active, onClick, icon, label, testId, accent }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`relative px-3 sm:px-5 py-3 text-xs sm:text-sm font-display flex items-center gap-2 transition-colors whitespace-nowrap shrink-0 min-h-[44px] ${
      active ? "text-white" : "text-slate-500 hover:text-slate-200"
    }`}
  >
    <span style={accent && active ? { color: accent } : undefined}>{icon}</span>
    <span>{label}</span>
    {active && (
      <span
        className="absolute left-3 right-3 -bottom-px h-0.5"
        style={{ backgroundColor: accent || "#D4AF37" }}
      />
    )}
  </button>
);
